import { Injectable } from '@angular/core';
import {
  chatDb,
  ChatDbConversation,
  IChatDbConversation,
  IChatDbMessage,
  IChatParticipant,
  IChatUser
} from '../classes/appdb';
import lodashMap from 'lodash-es/map';
import { Participant } from '@mitel/cloudlink-sdk/chat';
import Dexie from 'dexie';
import { MessageEx } from '../classes/messageex';
import { ChatLoggingService } from './chat-logging.service';
import { UserAvatars } from '@mitel/cloudlink-sdk/admin';

@Injectable({
  providedIn: 'root'
})
export class ChatDatabaseService {
  private messageLimit = 40;
  public indexDbEnabled = false;
  public indexDbError = false;
  private indexDbOpenCheckComplete = false;
  public conversationLimit = 40;
  private usersFetched = false;
  public synced = {};

  constructor(private chatLoggingService: ChatLoggingService) {}

  async openDb() {
    if (this.indexDbEnabled && !this.indexDbOpenCheckComplete) {
      this.indexDbOpenCheckComplete = true;
      try {
        await chatDb.open();
      } catch (ex) {
        this.indexDbEnabled = false;
        this.indexDbError = true;
        this.chatLoggingService.error({
          logMessage: 'chat-database.service.ctr() Failed to open chatDb',
          logParameters: [ex]
        });
      }
    }
  }

  public convertDbToMessage(m: IChatDbMessage, forceDbWrite?: boolean) {
    let message: MessageEx = {
      conversationId: m.conversationId,
      accountId: m.accountId,
      messageId: m.messageId,
      body: m.body,
      fallbackText: m.fallbackText,
      contentType: m.contentType,
      createdOn: <any>m.createdOn,
      createdBy: m.createdBy,
      modifiedOn: <any>m.modifiedOn,
      modifiedBy: m.modifiedBy,
      tag: m.tag,
      clCompChat_dbAttachmentName: m.attachmentName,
      clCompChat_dbAttachmentSize: m.attachmentSize,
      clCompChat_smsMessage: m.smsMessage,
      clCompChat_state: <any>m.state,
      _links: {}
    };
    if (forceDbWrite) {
      //race condition
      message.clCompChat_forceDatabaseWrite = true;
    }
    return message;
  }

  private convertMessageToDb(m: MessageEx) {
    let dbMessage: IChatDbMessage = {
      accountId: m.accountId,
      messageId: m.messageId,
      conversationId: m.conversationId,
      body: m.body,
      contentType: m.contentType,
      createdOn: m.createdOn ? m.createdOn.toString() : null,
      createdBy: m.createdBy,
      modifiedOn: m.modifiedOn ? m.modifiedOn.toString() : null,
      modifiedBy: m.modifiedBy,
      fallbackText: m.fallbackText,
      smsMessage: m.clCompChat_smsMessage,
      tag: m.tag
    };
    if (m.clCompChat_dbAttachmentName) {
      dbMessage.attachmentName = m.clCompChat_dbAttachmentName;
    }
    if (m.clCompChat_dbAttachmentSize) {
      dbMessage.attachmentSize = m.clCompChat_dbAttachmentSize;
    }
    if (m.clCompChat_state === 'failed' || m.clCompChat_state === 'pending') {
      dbMessage.state = m.clCompChat_state;
    }
    return dbMessage;
  }

  public async resyncConversation(conversationId: string, accountId: string, messages: MessageEx[]): Promise<void> {
    if (!this.indexDbEnabled || this.synced[conversationId]) {
      return;
    }

    await chatDb.transaction('rw', chatDb.conversations, chatDb.messages, async () => {
      let messagesToSave = messages.slice();
      let currentConversation = await this.getConversation(conversationId, accountId);
      if (currentConversation) {
        this.synced[conversationId] = true;
        let currentConversationMessages = currentConversation.messages.map(m => this.convertDbToMessage(m));
        currentConversationMessages.forEach(m => {
          if (m.clCompChat_state === 'failed') {
            messagesToSave.push(m);
          }
        });
        messagesToSave = messagesToSave.sort(
          (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
        );
        messagesToSave = messagesToSave.slice(-Math.abs(this.messageLimit));
        var dbConversation = new ChatDbConversation(conversationId, accountId, new Date().toISOString());
        dbConversation.messages = lodashMap(messagesToSave, (m: MessageEx) => this.convertMessageToDb(m));
        //Don't update the conversation, just a resync
        await dbConversation.save(true);
        this.chatLoggingService.info({
          logMessage: 'chat-database.service.resyncConversation() Resync completed',
          logParameters: [conversationId]
        });
      }
    });
  }

  public getConversations(accountId) {
    return chatDb.conversations
      .where('[accountId+conversationId]')
      .between([accountId, -Infinity], [accountId, Dexie.maxKey])
      .sortBy('indexDbLoadedOn');
  }

  public async deleteCheckedConversationFromDb(conversationId: string, accountId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    let match = await this.getConversationFromDb(conversationId, accountId);
    if (match) {
      await this.deleteConversationFromDb(match.conversationId, accountId);
    }
  }

  public async deleteConversationFromDb(conversationId: string, accountId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    this.chatLoggingService.debug({
      logMessage: 'chat-database.service.deleteConversationFromDb() Removing conversation',
      logParameters: [conversationId]
    });
    this.synced[conversationId] = false;
    await chatDb.conversations.delete([accountId, conversationId]);
    await chatDb.messages
      .where('conversationId')
      .equals(conversationId)
      .and(m => {
        return m.state !== 'pending';
      })
      .delete();
    await this.deleteConvParticipants(accountId, conversationId);
  }

  public async addConversationToDb(
    conversationId: string,
    accountId: string,
    messages: MessageEx[],
    lastReadMessageDate?: string
  ): Promise<void> {
    if (!this.indexDbEnabled) {
      return;
    }

    if (messages.length && messages[0].conversationId !== conversationId) {
      return;
    }

    this.synced[conversationId] = true;

    return chatDb.transaction('rw', chatDb.conversations, chatDb.messages, chatDb.participants, async () => {
      let existingConversations = await this.getConversations(accountId);

      //We only want to store the last x conversations for this account, so delete the oldest
      if (
        !existingConversations.find(c => c.conversationId === conversationId) &&
        existingConversations.length >= this.conversationLimit
      ) {
        let conversationsToDeleteCount = existingConversations.length - this.conversationLimit + 1;
        for (let i = 0; i < conversationsToDeleteCount; i++) {
          await this.deleteConversationFromDb(existingConversations[i].conversationId, accountId);
        }
      }

      var dbConversation = new ChatDbConversation(
        conversationId,
        accountId,
        new Date().toISOString(),
        lastReadMessageDate
      );
      dbConversation.messages = lodashMap(messages.slice(-40), (m: MessageEx) => this.convertMessageToDb(m));
      await dbConversation.save();
    });
  }

  public async getConversationFromDb(conversationId: string, accountId: string): Promise<IChatDbConversation> {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.conversations.get({
      accountId: accountId,
      conversationId: conversationId
    });
  }

  public async getConversation(conversationId: string, accountId: string): Promise<ChatDbConversation> {
    if (!this.indexDbEnabled) {
      return;
    }
    var dbConversation = new ChatDbConversation(conversationId, accountId);
    await dbConversation.loadNavigationProperties();
    return dbConversation;
  }

  public async updateConversationLastReadMessageDate(
    accountId: string,
    conversationId: string,
    lastReadMessageDate: string
  ) {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.conversations.update([accountId, conversationId], {
      lastReadMessageDate: lastReadMessageDate
    });
  }

  public getPendingSendMessages(): Promise<IChatDbMessage[]> {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.messages.where('state').equals('pending').sortBy('createdOn');
  }

  public async getMessages(conversationId: string, accountId: string): Promise<IChatDbMessage[]> {
    if (!this.indexDbEnabled) {
      return;
    }
    let messages = await chatDb.messages
      .where({
        accountId: accountId,
        conversationId: conversationId
      })
      .toArray();
    messages = messages.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
    return messages;
  }

  public async updateMessage(message: MessageEx): Promise<void> {
    if (!this.indexDbEnabled) {
      return;
    }
    await chatDb.messages.update([message.accountId, message.messageId], this.convertMessageToDb(message));
  }

  public async deleteMessage(accountId: string, messageId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.messages.delete([accountId, messageId]);
  }

  public async updateAttachmentMessage(
    messageId: string,
    accountId: string,
    attachmentName: string,
    attachmentSize: string
  ): Promise<number> {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.messages.update([accountId, messageId], {
      attachmentName: attachmentName,
      attachmentSize: attachmentSize
    });
  }

  public async addUser(
    principalId: string,
    accountId: string,
    displayName: string,
    isLoggedIn: boolean,
    email?: string,
    avatars?: UserAvatars
  ): Promise<[string, string]> {
    // AI/JSON modes skip claims, so there's no accountId to build the [accountId+principalId] key
    if (!this.indexDbEnabled || !accountId) {
      return;
    }
    if (isLoggedIn) {
      //Set last logged in user to false
      let lastLoggedInUser = await this.getLoggedInUser();
      if (lastLoggedInUser && lastLoggedInUser.principalId !== principalId) {
        await chatDb.users.update([lastLoggedInUser.accountId, lastLoggedInUser.principalId], { isLoggedIn: 'false' });
      }
      return chatDb.users.put({
        accountId: accountId,
        principalId: principalId,
        indexDbLoadedOn: new Date().toISOString(),
        displayName: displayName,
        email: email,
        avatars: avatars,
        isLoggedIn: 'true'
      });
    }
    return chatDb.users.put({
      accountId: accountId,
      principalId: principalId,
      indexDbLoadedOn: new Date().toISOString(),
      displayName: displayName,
      email: email,
      avatars: avatars
    });
  }

  public async deleteUser(accountId: string, principalId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.users.delete([accountId, principalId]);
  }

  public async getLoggedInUser(): Promise<IChatUser> {
    if (!this.indexDbEnabled) {
      return;
    }
    let lastLoggedInUser = await chatDb.users
      .where({
        isLoggedIn: 'true'
      })
      .first();
    return lastLoggedInUser;
  }

  public async loadUsers(): Promise<IChatUser[]> {
    if (!this.indexDbEnabled || this.usersFetched) {
      return;
    }
    this.chatLoggingService.info({
      logMessage: 'chat-database.service.loadUsers()'
    });
    this.usersFetched = true;
    return this.getUsers();
  }

  public async getUsers(): Promise<IChatUser[]> {
    if (!this.indexDbEnabled) {
      return;
    }
    let chatDbUsers = await chatDb.users.toArray();
    return chatDbUsers;
  }

  public async searchUsers(search: string): Promise<IChatUser[]> {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.users
      .where('displayName')
      .startsWithIgnoreCase(search)
      .or('email')
      .startsWithIgnoreCase(search)
      .limit(10)
      .toArray();
  }

  public async addMessageIfConversationExists(message: MessageEx): Promise<any> {
    if (!this.indexDbEnabled) {
      return;
    }
    //We always store pending messages
    if (message.clCompChat_state === 'pending') {
      await this.addMessage(message);
      return true;
    } else {
      var conversationFromDb = await this.getConversationFromDb(message.conversationId, message.accountId);
      if (!!conversationFromDb) {
        await this.addMessage(message);
        return true;
      }
      return false;
    }
  }

  public async addMessage(message: MessageEx): Promise<void> {
    if (!this.indexDbEnabled) {
      return;
    }
    var existingMessages = await chatDb.messages
      .where({
        accountId: message.accountId,
        conversationId: message.conversationId
      })
      .sortBy('createdOn');

    if (existingMessages.find(m => m.messageId === message.messageId)) {
      await this.updateMessage(message);
    } else if (existingMessages.length && new Date(existingMessages[0].createdOn) > new Date(message.createdOn)) {
      return;
    } else if (existingMessages.length < this.messageLimit) {
      await chatDb.messages.put(this.convertMessageToDb(message));
    } else {
      var oldestMessage = existingMessages[0];
      await chatDb.transaction('rw', chatDb.messages, async () => {
        await chatDb.messages.delete([oldestMessage.accountId, oldestMessage.messageId]);
        await chatDb.messages.put(this.convertMessageToDb(message));
      });
    }
  }

  public async addParticipantIfConversationExists(participant: Participant): Promise<any> {
    if (!this.indexDbEnabled) {
      return false;
    }
    let conversationFromDb = await this.getConversationFromDb(participant.conversationId, participant.accountId);
    if (!!conversationFromDb) {
      await this.addParticipant(
        participant.accountId,
        participant.conversationId,
        participant.userId,
        participant.lastReadMessageDate?.toString(),
        participant.participantType
      );
      return true;
    }
    return false;
  }

  public async addParticipant(
    accountId: string,
    conversationId: string,
    principalId: string,
    lastReadMessageDate: string,
    participantType: string
  ): Promise<[string, string, string]> {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.participants.put({
      accountId: accountId,
      conversationId: conversationId,
      principalId: principalId,
      lastReadMessageDate: lastReadMessageDate,
      participantType: participantType
    });
  }

  public async deleteParticipant(accountId: string, conversationId: string, principalId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    return chatDb.participants.delete([accountId, conversationId, principalId]);
  }

  public async deleteConvParticipants(accountId: string, conversationId: string) {
    if (!this.indexDbEnabled) {
      return;
    }
    await chatDb.participants
      .where('[accountId+conversationId+principalId]')
      .between([accountId, conversationId, Dexie.minKey], [accountId, conversationId, Dexie.maxKey])
      .delete();
  }

  public async getParticipants(accountId: string, conversationId: string): Promise<IChatParticipant[]> {
    if (!this.indexDbEnabled) {
      return;
    }
    let participants = await chatDb.participants
      .where('[accountId+conversationId+principalId]')
      .between([accountId, conversationId, Dexie.minKey], [accountId, conversationId, Dexie.maxKey])
      .toArray();
    return participants;
  }
}
